import { useState, HTMLAttributes, useRef, useEffect, MouseEvent, useMemo } from 'react';
import arrow from '../../assets/icons/arrow.svg';
import errorIcon from '../../assets/icons/error.svg';
import { useSafeSelector } from '../../hooks/useSafeSelector';
import { borderStyleConstants } from '../../constants/borderStyle';
import { getPhoneMaxLength } from '../../utils/getPhoneMaxLength';
import { restCountries } from '../../constants/restCountries';
import searchIcon from '../../assets/icons/search-icon.svg';
import check from '../../assets/icons/check.svg';
import { useCloseDropdown } from '../../hooks/useCloseDropdown';
export interface PhoneFormInputProps {
  placeholder?: string;
  type?: string;
  maxLength?: number;
  name: string;
  error?: any;
  className?: string;
  inputMode?: HTMLAttributes<HTMLElement>['inputMode'];
  phoneFormRegister: any;
  selectedCountry: { name: string; code: string; currency?: string; iso?: string };
  setSelectedCountry: any;
  disabled?: boolean;
  customStyle?: any;
  borderRadius?: any;
  disableDropdown?: boolean;
  trailingIcon?: JSX.Element;
  currencyCodes?: string[];
  verified?: boolean;
  autoFocus?: boolean;
  allowedCountries?: string[];
  sortByAllowedCountries?: boolean;
}

export function PhoneFormInput(props: PhoneFormInputProps) {
  const {
    phoneFormRegister,
    name,
    placeholder,
    maxLength,
    type,
    inputMode,
    error,
    disabled,
    selectedCountry,
    setSelectedCountry,
    customStyle,
    className,
    disableDropdown,
    trailingIcon,
    currencyCodes,
    verified,
    autoFocus,
    allowedCountries,
    sortByAllowedCountries
  } = props;
  
  const [showDropdown, setShowDropdown] = useState(false);
  const [search, setSearch] = useState('');
  const [countries, setCountries] = useState(restCountries);
  const { borderStyle } = useSafeSelector((state: any) => state.customization);
  const lang = useSafeSelector((state: any) => state.lang);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [typedLetter, setTypedLetter] = useState('');
  const optionsRef = useRef<any[]>([]);
  const inputContainerRef = useCloseDropdown(setShowDropdown);
  const isRTL = lang === 'ar';

  // matched on the iso first so the flag cannot drift from the dial code shown
  const currentCountry = useMemo(
    () =>
      countries.find((country) => country.iso === selectedCountry?.iso) ||
      countries.find(
        (country) =>
          country.name.toLowerCase() === selectedCountry?.name?.toLowerCase()
      ) ||
      countries[0],
    [countries, selectedCountry?.iso, selectedCountry?.name]
  );

  useEffect(() => {
    if (currencyCodes?.length) {
      const countries = restCountries.filter((country) =>
        currencyCodes.includes(country.currency)
      );
      setCountries(countries);

      const currencyToUse = currencyCodes.includes(
        selectedCountry.currency || ''
      )
        ? selectedCountry.currency
        : currencyCodes[0];
      const nextCountry = countries.find(
        (country) => country.currency === currencyToUse
      );
      // keep the current country rather than clearing it when nothing matches
      if (nextCountry) setSelectedCountry(nextCountry);
    } else if (allowedCountries?.length) {
      let countries = restCountries.filter((country) =>
        allowedCountries.includes(country.iso)
      );

      if (sortByAllowedCountries) {
        countries = [...countries].sort((a, b) => {
          return (
            allowedCountries.indexOf(a.iso) - allowedCountries.indexOf(b.iso)
          );
        });
      }

      setCountries(countries);

      const isoToUse = allowedCountries.includes(selectedCountry.iso || '')
        ? selectedCountry.iso
        : allowedCountries[0];
      const nextCountry = countries.find((country) => country.iso === isoToUse);
      // keep the current country rather than clearing it when nothing matches
      if (nextCountry) setSelectedCountry(nextCountry);
    }
  }, []);

  useEffect(() => {
    if (showDropdown && typedLetter) {
      const index = countries.findIndex((option) =>
        option.name.toLowerCase().startsWith(typedLetter.toLowerCase())
      );
      if (index !== -1) {
        setHighlightedIndex(index);
      }
    }
  }, [typedLetter, countries, showDropdown]);

  useEffect(() => {
    if (!showDropdown || highlightedIndex === -1) return;
    if (optionsRef.current[highlightedIndex]) {
      optionsRef.current[highlightedIndex].scrollIntoView({ block: 'nearest' });
    }
    setTypedLetter('');
    setHighlightedIndex(-1);
  }, [highlightedIndex, showDropdown]);

  const handleKeyDown = (e: any) => {
    const letter = e.key;
    if (letter.length === 1 && letter.match(/[a-zA-Z]/)) {
      setTypedLetter(letter);
      setTimeout(() => setTypedLetter(''), 500);
    }
  };

  const toggleDropdown = (e: MouseEvent<HTMLButtonElement>) => {
    if (e.detail === 0) return;
    e.preventDefault();
    e.stopPropagation();
    setSearch('');
    setShowDropdown((prevState) => !prevState);
  };

  const handleCountrySelect = (country: any) => {
    setSelectedCountry(country);
    setShowDropdown(false);
  };

  const handleCountrySearch = (e: any) => {
    setSearch(e.target.value);
  };

  const filteredCountries = useMemo(() => {
    const query = search.toLowerCase().trim();

    return countries.filter(
      (country) =>
        country.name.toLowerCase().includes(query) ||
        country.code.toLowerCase().includes(query)
    );
  }, [countries, search]);

  return (
    <div ref={inputContainerRef} className="relative flex flex-col w-full">
      <div
        className={`bg-white px-3 box-border border border-solid ${
          borderStyle === borderStyleConstants.RECTANGULAR && '!rounded-none'
        } flex justify-between items-center w-full h-10 opacity-100 relative rounded-lg ${
          className ?? ''
        }`}
        style={{...customStyle, direction:"ltr" }}
      >
        <button
          className="flex items-center w-auto h-full gap-1 outline-none"
          onClick={toggleDropdown}
          onKeyDown={handleKeyDown}
          disabled={disableDropdown}
        >
          <img
            src={currentCountry?.icon}
            width={20}
            height={20}
            className="max-w-none"
            alt={'country icon'}
          />{' '}
          {/* Show country icon */}
          <label className="w-auto mx-1 text-center">
            {selectedCountry?.code}
          </label>
          {!disableDropdown && (
            <img
              src={arrow}
              width={8}
              height={6}
              className={`w-3 max-w-none transition-all ${
                showDropdown ? 'rotate-180 mt-[2px]' : 'mt-1'
              }`}
              alt={'arrow icon'}
            />
          )}
        </button>
        <input
          id={name}
          name={name}
          type={type}
          className={ 'text-left pl-2 !placeholder:text-gray-500 bg-transparent w-full h-full border-0 box-border text-inherit focus:outline-none focus-visible:outline-none'}
          {...phoneFormRegister}
          placeholder={placeholder ? placeholder : ''}
          maxLength={maxLength ?? getPhoneMaxLength(selectedCountry.name)}
          autoComplete="off"
          inputMode={inputMode}
          disabled={disabled}
          onClick={() => setShowDropdown(false)}
          autoFocus={autoFocus}
        />
        {verified && (
          <span
            className="bg-[#E6FEEE] text-[#69BE76] ms-2 font-Montserrat font-black rounded-sm text-[10px] leading-[10px] -tracing-[.2px] px-1 py-0.5
"
          >
            Verified
          </span>
        )}

        {trailingIcon}
        {error && <img className="w-3 h-3" src={errorIcon} alt="error" />}
      </div>
      {showDropdown && (
        <ul
          tabIndex={-1}
          className="absolute z-20 w-full py-2 overflow-y-auto bg-white border rounded-lg shadow-lg top-10 max-h-56"
        >
          <div className="border rounded-lg border-gray-300 mx-auto w-[calc(100%-1rem)] text-gray-500 flex gap-2 items-center py-2 px-2 mb-2">
            <img src={searchIcon} alt="" />
            <input
              type="text"
              value={search}
              className="flex-shrink w-full outline-none focus:outline-none"
              placeholder="Search for country"
              onChange={handleCountrySearch}
            />
          </div>
          {filteredCountries.map((country, index) => (
            <li
              key={index}
              className={`flex items-center p-3 hover:bg-gray-100 cursor-pointer ${
                selectedCountry.code === country.code ? 'bg-gray-50' : ''
              }`}
              onClick={() => handleCountrySelect(country)}
              ref={(el) => (optionsRef.current[index] = el)}
            >
              <img src={country.icon} width={24} height={24} className="w-auto h-6" alt="" />
              <span className="ml-2">{country.name}</span>
              {selectedCountry.code === country.code && (
                <img className="ml-auto" src={check} alt="checked" />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
